/**
 * OnboardingTab — onboarding flow admin (preview, steps, defaults).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, View, Text, TouchableOpacity, StyleSheet, TextInput, ScrollView } from 'react-native';
import { db } from '../firebaseConfig';
import { collection, doc, getDocs, onSnapshot, setDoc } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import OnboardingFlow from './OnboardingFlow';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';
import { uiVar } from './floodlight/uiStyleCss';

const ONBOARDING_STEPS_META = [
  { step: 0, title: 'Welcome',      desc: 'Cinematic intro with tier badge' },
  { step: 1, title: 'Profile',      desc: 'New / Recreational / Sharp selector' },
  { step: 2, title: 'My Books',     desc: 'Select preferred sportsbooks' },
  { step: 3, title: 'Settings',     desc: 'Unit size + EV threshold' },
  { step: 4, title: 'Sports',       desc: 'Favorite sports for personalized content' },
  { step: 5, title: 'Done',         desc: 'Summary and launch' },
];

const DEFAULT_OB_BOOKS = [
  'fanduel','draftkings','betmgm','caesars','espn_bet','fanatics','bet365','betrivers',
  'hard_rock','pointsbet','wynnbet','pinnacle','betway','unibet','bovada','betonline',
  'mybookie','superbook','tipico','betfred','circa','fliff','underdog','sleeper',
];
const DEFAULT_OB_SPORTS  = ['nba','nfl','mlb','nhl','ncaab','ncaaf','soccer','tennis','ufc','golf'];
const DEFAULT_OB_UNITS   = ['10','25','50','100','250'];
const DEFAULT_OB_FEATURES = [
  { icon: 'analytics-outline', title: 'Smart EV Analysis',      desc: 'Find +EV bets across 300+ sportsbooks' },
  { icon: 'flash-outline',     title: 'Natural Language Search', desc: 'Search like "LeBron points over 25"' },
  { icon: 'calculator-outline',title: 'Kelly Criterion',         desc: 'Optimized bet sizing for your bankroll' },
];


export default function OnboardingTab() {
  const [subTab, setSubTab] = useState('preview');

  // ── Enabled toggle — lives outside sub-tabs, always visible ─────────────────
  const [onboardingEnabled, setOnboardingEnabled] = useState(true);
  const [enabledLoading, setEnabledLoading]       = useState(true);
  const [enabledSaving, setEnabledSaving]         = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'site_config', 'onboarding'), snap => {
      const d = snap.exists() ? snap.data() : {};
      // Default to enabled:true if field not set yet
      setOnboardingEnabled(d.enabled !== false);
      setEnabledLoading(false);
    }, () => setEnabledLoading(false));
    return () => unsub();
  }, []);

  const toggleEnabled = async () => {
    const next = !onboardingEnabled;
    setEnabledSaving(true);
    setOnboardingEnabled(next); // optimistic
    try {
      await setDoc(doc(db, 'site_config', 'onboarding'), { enabled: next }, { merge: true });
      showToast(`Onboarding ${next ? 'enabled' : 'disabled'} for new users.`, next ? 'success' : 'info');
    } catch (e) {
      setOnboardingEnabled(!next); // revert
      showToast('Failed to update: ' + e.message, 'error');
    } finally {
      setEnabledSaving(false);
    }
  };

  // ── Preview state ───────────────────────────────────────────────────────────
  const [previewActive, setPreviewActive]   = useState(false);
  const [previewStep, setPreviewStep]       = useState(0);
  const [deviceFrame, setDeviceFrame]       = useState('mobile');
  const [previewKey, setPreviewKey]         = useState(0);
  const [previewTier, setPreviewTier]       = useState('alpha'); // preview as this tier

  // ── Analytics state ─────────────────────────────────────────────────────────
  const [analytics, setAnalytics]   = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      let total = 0, completed = 0, skipped = 0;
      const stepCounts = [0, 0, 0, 0, 0];
      const recent = [];

      usersSnap.forEach(d => {
        const u = d.data();
        total++;
        if (u.onboardingCompleted) {
          completed++;
          recent.push({
            name: u.displayName || u.email || d.id,
            email: u.email || '',
            completedAt: u.onboardingCompletedAt || '',
            books: (u['preferences.selectedSportsbooks'] || []).length,
          });
        }
        if (u.onboardingSkipped) skipped++;
        if (typeof u.onboardingStep === 'number') {
          const s = Math.min(u.onboardingStep, 4);
          stepCounts[s]++;
        }
      });

      recent.sort((a, b) => b.completedAt.localeCompare(a.completedAt));
      setAnalytics({ total, completed, skipped, pending: total - completed - skipped, stepCounts, recent: recent.slice(0, 20) });
    } catch (e) {
      showToast('Failed to load analytics: ' + e.message, 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => { if (subTab === 'analytics') loadAnalytics(); }, [subTab]);

  // ── Config state ─────────────────────────────────────────────────────────────
  const [config, setConfig]       = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving]   = useState(false);
  const [localConfig, setLocalConfig]     = useState(null); // editable copy

  useEffect(() => {
    if (subTab !== 'config') return;
    setConfigLoading(true);
    const unsub = onSnapshot(doc(db, 'site_config', 'onboarding'), snap => {
      const d = snap.exists() ? snap.data() : {};
      setConfig(d);
      setLocalConfig(JSON.parse(JSON.stringify({
        books: d.books || DEFAULT_OB_BOOKS.map(id => ({ id, enabled: true })),
        sports: d.sports || DEFAULT_OB_SPORTS.map(id => ({ id, enabled: true })),
        unitSizeOptions: d.unitSizeOptions || DEFAULT_OB_UNITS,
        welcomeFeatures: d.welcomeFeatures || DEFAULT_OB_FEATURES,
      })));
      setConfigLoading(false);
    }, () => setConfigLoading(false));
    return () => unsub();
  }, [subTab]);

  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'onboarding'), localConfig, { merge: true });
      showToast('Onboarding config saved.', 'success');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setConfigSaving(false);
    }
  };

  const resetConfig = async () => {
    const defaults = {
      books: DEFAULT_OB_BOOKS.map(id => ({ id, enabled: true })),
      sports: DEFAULT_OB_SPORTS.map(id => ({ id, enabled: true })),
      unitSizeOptions: DEFAULT_OB_UNITS,
      welcomeFeatures: DEFAULT_OB_FEATURES,
    };
    setLocalConfig(defaults);
    setConfigSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'onboarding'), defaults);
      showToast('Config reset to defaults.', 'success');
    } catch (e) {
      showToast('Reset failed: ' + e.message, 'error');
    } finally {
      setConfigSaving(false);
    }
  };

  const toggleBookEnabled = (id) => {
    setLocalConfig(prev => ({
      ...prev,
      books: prev.books.map(b => b.id === id ? { ...b, enabled: !b.enabled } : b),
    }));
  };

  const toggleSportEnabled = (id) => {
    setLocalConfig(prev => ({
      ...prev,
      sports: prev.sports.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s),
    }));
  };

  const frameWidth = deviceFrame === 'mobile' ? 390 : deviceFrame === 'tablet' ? 768 : '100%';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <View style={styles.tabWrap}>

      {/* ── Enabled / Disabled banner ────────────────────────────────────────── */}
      <View style={[obStyles.enabledBanner, onboardingEnabled ? obStyles.enabledBannerOn : obStyles.enabledBannerOff]}>
        <View style={{ flex: 1 }}>
          <Text style={obStyles.enabledBannerTitle}>
            {onboardingEnabled ? 'Onboarding is LIVE' : 'Onboarding is DISABLED'}
          </Text>
          <Text style={obStyles.enabledBannerSub}>
            {onboardingEnabled
              ? 'New users will see the onboarding flow after registration.'
              : 'New users bypass onboarding and go directly to the Home screen.'}
          </Text>
        </View>
        <TouchableOpacity
          onPress={enabledSaving || enabledLoading ? undefined : toggleEnabled}
          activeOpacity={0.85}
          style={[obStyles.toggleTrack, onboardingEnabled ? obStyles.toggleTrackOn : obStyles.toggleTrackOff]}
        >
          {(enabledSaving || enabledLoading)
            ? <ActivityIndicator size="small" color="#fff" style={{ margin: 4 }} />
            : <View style={[obStyles.toggleThumb, onboardingEnabled ? obStyles.toggleThumbOn : obStyles.toggleThumbOff]} />}
        </TouchableOpacity>
      </View>

      {/* Sub-tab bar */}
      <View style={{ flexDirection: 'row', gap: 6, marginBottom: 20 }}>
        {[
          { key: 'preview',   label: 'Preview',   icon: 'phone-portrait-outline' },
          { key: 'analytics', label: 'Analytics', icon: 'bar-chart-outline' },
          { key: 'config',    label: 'Config',    icon: 'settings-outline' },
        ].map(t => (
          <TouchableOpacity key={t.key} onPress={() => setSubTab(t.key)}
            style={[obStyles.subTabBtn, subTab === t.key && obStyles.subTabBtnActive]}>
            <Ionicons name={t.icon} size={13} color={subTab === t.key ? uiVar('adm-accent', '#8B5CF6') : 'rgba(255,255,255,0.45)'} />
            <Text style={[obStyles.subTabText, subTab === t.key && { color: uiVar('adm-accent', '#8B5CF6') }]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* ── PREVIEW ── */}
      {subTab === 'preview' && (
        <View style={{ flex: 1 }}>
          {!previewActive ? (
            <View>
              {/* Device frame + tier toggles */}
              <View style={{ flexDirection: 'row', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {['mobile', 'tablet', 'desktop'].map(f => (
                  <TouchableOpacity key={f} onPress={() => setDeviceFrame(f)}
                    style={[obStyles.frameBtn, deviceFrame === f && obStyles.frameBtnActive]}>
                    <Ionicons name={f === 'mobile' ? 'phone-portrait-outline' : f === 'tablet' ? 'tablet-portrait-outline' : 'desktop-outline'} size={13} color={deviceFrame === f ? uiVar('adm-ink', '#fff') : 'rgba(255,255,255,0.4)'} />
                    <Text style={[obStyles.frameBtnText, deviceFrame === f && { color: uiVar('adm-ink', '#fff') }]}>{f.charAt(0).toUpperCase() + f.slice(1)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={{ flexDirection: 'row', gap: 6, marginBottom: 16 }}>
                {[{ id: 'alpha', label: 'Alpha', color: '#fbbf24' }, { id: 'beta', label: 'Beta', color: '#a78bfa' }, { id: 'standard', label: 'Standard', color: 'rgba(255,255,255,0.5)' }].map(t => (
                  <TouchableOpacity key={t.id} onPress={() => setPreviewTier(t.id)}
                    style={[obStyles.frameBtn, previewTier === t.id && { borderColor: t.color, backgroundColor: `${t.color}15` }]}>
                    <Text style={[obStyles.frameBtnText, previewTier === t.id && { color: t.color }]}>As {t.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Step jump buttons */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                <TouchableOpacity style={styles.btn} onPress={() => { setPreviewStep(0); setPreviewKey(k => k + 1); setPreviewActive(true); }}>
                  <Text style={styles.btnText}>Preview from Start</Text>
                </TouchableOpacity>
                {ONBOARDING_STEPS_META.filter(s => s.step > 0).map(s => (
                  <TouchableOpacity key={s.step}
                    style={[styles.smallBtn, { backgroundColor: 'rgba(255,255,255,0.08)' }]}
                    onPress={() => { setPreviewStep(s.step); setPreviewKey(k => k + 1); setPreviewActive(true); }}>
                    <Text style={styles.smallBtnText}>Step {s.step + 1}: {s.title}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Step overview */}
              <View style={obStyles.stepList}>
                {ONBOARDING_STEPS_META.map((s, idx) => (
                  <View key={idx} style={obStyles.stepRow}>
                    <View style={obStyles.stepNumBubble}>
                      <Text style={obStyles.stepNum}>{s.step + 1}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={obStyles.stepTitle}>{s.title}</Text>
                      <Text style={obStyles.stepDesc}>{s.desc}</Text>
                    </View>
                    <TouchableOpacity onPress={() => { setPreviewStep(s.step); setPreviewKey(k => k + 1); setPreviewActive(true); }}
                      style={obStyles.jumpBtn}>
                      <Ionicons name="play-outline" size={12} color={uiVar('adm-accent', '#8B5CF6')} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            </View>
          ) : deviceFrame === 'desktop' ? (
            // Desktop: full-screen fixed overlay — exactly what the user sees
            <View style={obStyles.desktopFullscreen}>
              <OnboardingFlow
                key={previewKey}
                isPreview={true}
                initialStep={previewStep}
                userTier={previewTier}
                onComplete={(prefs) => { setPreviewActive(false); showToast('Preview complete.', 'success'); console.log('[Preview prefs]', prefs); }}
                onSkip={() => setPreviewActive(false)}
              />
              <TouchableOpacity onPress={() => setPreviewActive(false)} style={obStyles.desktopExitBtn}>
                <Ionicons name="close" size={15} color={uiVar('adm-ink', '#fff')} />
                <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '700' }}>Exit</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={{ alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 10 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                  Previewing as: {deviceFrame} frame
                </Text>
                <TouchableOpacity onPress={() => setPreviewActive(false)} style={obStyles.exitBtn}>
                  <Ionicons name="close" size={14} color={uiVar('adm-ink', '#fff')} />
                  <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '600' }}>Exit Preview</Text>
                </TouchableOpacity>
              </View>
              <View style={[obStyles.previewFrame, { width: frameWidth, maxWidth: '100%' }]}>
                <OnboardingFlow
                  key={previewKey}
                  isPreview={true}
                  initialStep={previewStep}
                  userTier={previewTier}
                  onComplete={(prefs) => { setPreviewActive(false); showToast('Preview complete — check console for prefs.', 'success'); console.log('[Preview prefs]', prefs); }}
                  onSkip={() => setPreviewActive(false)}
                />
              </View>
            </View>
          )}
        </View>
      )}

      {/* ── ANALYTICS ── */}
      {subTab === 'analytics' && (
        <View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text style={styles.sectionTitle}>Onboarding Analytics</Text>
            <TouchableOpacity onPress={loadAnalytics} style={obStyles.refreshBtn} disabled={analyticsLoading}>
              {analyticsLoading ? <ActivityIndicator size="small" color="#8B5CF6" /> : <><Ionicons name="refresh-outline" size={14} color={uiVar('adm-accent', '#8B5CF6')} /><Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12 }}>Refresh</Text></>}
            </TouchableOpacity>
          </View>

          {analyticsLoading && !analytics && (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <ActivityIndicator color="#8B5CF6" size="large" />
              <Text style={{ color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>Loading analytics…</Text>
            </View>
          )}

          {analytics && (
            <>
              {/* Summary cards */}
              <View style={obStyles.statsRow}>
                {[
                  { label: 'Total Users',  value: analytics.total,     color: '#60a5fa' },
                  { label: 'Completed',    value: analytics.completed,  color: '#4ade80', pct: analytics.total ? Math.round(analytics.completed / analytics.total * 100) : 0 },
                  { label: 'Skipped',      value: analytics.skipped,    color: '#fbbf24', pct: analytics.total ? Math.round(analytics.skipped  / analytics.total * 100) : 0 },
                  { label: 'Pending',      value: analytics.pending,    color: '#f87171', pct: analytics.total ? Math.round(analytics.pending   / analytics.total * 100) : 0 },
                ].map(s => (
                  <View key={s.label} style={obStyles.statCard}>
                    <Text style={[obStyles.statValue, { color: s.color }]}>{s.value}</Text>
                    <Text style={obStyles.statLabel}>{s.label}</Text>
                    {s.pct !== undefined && <Text style={[obStyles.statPct, { color: s.color }]}>{s.pct}%</Text>}
                  </View>
                ))}
              </View>

              {/* Step funnel */}
              <View style={obStyles.funnelCard}>
                <Text style={obStyles.funnelTitle}>Step Progress Funnel</Text>
                <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginBottom: 12 }}>Users who reached each step (tracked in real time)</Text>
                {ONBOARDING_STEPS_META.map((s, i) => {
                  const count = analytics.stepCounts[i] || 0;
                  const max = Math.max(...analytics.stepCounts, 1);
                  const pct = Math.round(count / max * 100);
                  return (
                    <View key={i} style={{ marginBottom: 10 }}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Step {i + 1}: {s.title}</Text>
                        <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12, fontWeight: '700' }}>{count}</Text>
                      </View>
                      <View style={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)' }}>
                        <View style={{ height: 6, borderRadius: 3, width: `${pct}%`, backgroundColor: uiVar('adm-accent-fill', '#8B5CF6') }} />
                      </View>
                    </View>
                  );
                })}
              </View>

              {/* Recent completions */}
              <Text style={[styles.sectionTitle, { fontSize: 14, marginBottom: 10 }]}>Recent Completions</Text>
              {analytics.recent.length === 0 && <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>No completions yet.</Text>}
              {analytics.recent.map((u, i) => (
                <View key={i} style={obStyles.recentRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '500' }}>{u.name}</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{u.email}</Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{u.completedAt ? new Date(u.completedAt).toLocaleDateString() : ''}</Text>
                    <Text style={{ color: '#4ade80', fontSize: 11 }}>{u.books} books</Text>
                  </View>
                </View>
              ))}
            </>
          )}
        </View>
      )}

      {/* ── CONFIG ── */}
      {subTab === 'config' && (
        <View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Text style={styles.sectionTitle}>Onboarding Config</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={resetConfig} style={[obStyles.cfgBtn, { borderColor: 'rgba(248,113,113,0.4)' }]}>
                <Text style={{ color: '#f87171', fontSize: 12 }}>Reset Defaults</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={saveConfig} disabled={configSaving} style={[obStyles.cfgBtn, { borderColor: uiVar('adm-accent-wash-60', 'rgba(139,92,246,0.6)'), backgroundColor: uiVar('adm-accent-wash-12', 'rgba(139,92,246,0.12)') }]}>
                {configSaving ? <ActivityIndicator size="small" color="#8B5CF6" /> : <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12, fontWeight: '700' }}>Save Config</Text>}
              </TouchableOpacity>
            </View>
          </View>

          {configLoading || !localConfig ? (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <ActivityIndicator color="#8B5CF6" size="large" />
            </View>
          ) : (
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Welcome features */}
              <View style={obStyles.cfgSection}>
                <Text style={obStyles.cfgSectionTitle}>Welcome Screen Features</Text>
                <Text style={obStyles.cfgSectionDesc}>Bullet points shown on the first step.</Text>
                {localConfig.welcomeFeatures.map((feat, idx) => (
                  <View key={idx} style={obStyles.cfgFeatureRow}>
                    <View style={{ flex: 1, gap: 4 }}>
                      <TextInput
                        style={obStyles.cfgInput}
                        value={feat.title}
                        onChangeText={v => setLocalConfig(p => ({ ...p, welcomeFeatures: p.welcomeFeatures.map((f, i) => i === idx ? { ...f, title: v } : f) }))}
                        placeholder="Feature title"
                        placeholderTextColor="rgba(255,255,255,0.25)"
                      />
                      <TextInput
                        style={[obStyles.cfgInput, { fontSize: 11 }]}
                        value={feat.desc}
                        onChangeText={v => setLocalConfig(p => ({ ...p, welcomeFeatures: p.welcomeFeatures.map((f, i) => i === idx ? { ...f, desc: v } : f) }))}
                        placeholder="Short description"
                        placeholderTextColor="rgba(255,255,255,0.25)"
                      />
                    </View>
                    <TouchableOpacity onPress={() => setLocalConfig(p => ({ ...p, welcomeFeatures: p.welcomeFeatures.filter((_, i) => i !== idx) }))}
                      style={{ paddingLeft: 8, paddingTop: 4 }}>
                      <Ionicons name="trash-outline" size={16} color="#f87171" />
                    </TouchableOpacity>
                  </View>
                ))}
                <TouchableOpacity onPress={() => setLocalConfig(p => ({ ...p, welcomeFeatures: [...p.welcomeFeatures, { icon: 'star-outline', title: '', desc: '' }] }))}
                  style={obStyles.cfgAddBtn}>
                  <Ionicons name="add-circle-outline" size={14} color={uiVar('adm-accent', '#8B5CF6')} />
                  <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12 }}>Add Feature</Text>
                </TouchableOpacity>
              </View>

              {/* Books */}
              <View style={obStyles.cfgSection}>
                <Text style={obStyles.cfgSectionTitle}>Sportsbooks Shown (Step 2)</Text>
                <Text style={obStyles.cfgSectionDesc}>Toggle which books appear in the My Books selection grid.</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {localConfig.books.map(b => (
                    <TouchableOpacity key={b.id} onPress={() => toggleBookEnabled(b.id)}
                      style={[obStyles.cfgChip, b.enabled && obStyles.cfgChipActive]}>
                      <Text style={[obStyles.cfgChipText, b.enabled && { color: uiVar('adm-accent', '#8B5CF6') }]}>{b.id}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 8 }}>
                  {localConfig.books.filter(b => b.enabled).length} of {localConfig.books.length} enabled
                </Text>
              </View>

              {/* Sports */}
              <View style={obStyles.cfgSection}>
                <Text style={obStyles.cfgSectionTitle}>Sports Shown (Step 4)</Text>
                <Text style={obStyles.cfgSectionDesc}>Toggle which sports appear in the Sports Preferences grid.</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {localConfig.sports.map(s => (
                    <TouchableOpacity key={s.id} onPress={() => toggleSportEnabled(s.id)}
                      style={[obStyles.cfgChip, s.enabled && obStyles.cfgChipActive]}>
                      <Text style={[obStyles.cfgChipText, s.enabled && { color: uiVar('adm-accent', '#8B5CF6') }]}>{s.id.toUpperCase()}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Unit sizes */}
              <View style={obStyles.cfgSection}>
                <Text style={obStyles.cfgSectionTitle}>Unit Size Options (Step 3)</Text>
                <Text style={obStyles.cfgSectionDesc}>Comma-separated dollar values shown as quick-select buttons.</Text>
                <TextInput
                  style={obStyles.cfgInput}
                  value={localConfig.unitSizeOptions.join(',')}
                  onChangeText={v => setLocalConfig(p => ({ ...p, unitSizeOptions: v.split(',').map(s => s.trim()).filter(Boolean) }))}
                  placeholder="10,25,50,100,250"
                  placeholderTextColor="rgba(255,255,255,0.25)"
                />
              </View>
            </ScrollView>
          )}
        </View>
      )}
    </View>
  );
}

// Onboarding admin sub-styles (scoped to keep main styles clean)
const obStyles = StyleSheet.create({
  subTabBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  subTabBtnActive: {
    borderColor: uiVar('adm-accent-wash-50', 'rgba(139,92,246,0.5)'),
    backgroundColor: uiVar('adm-accent-wash-10', 'rgba(139,92,246,0.1)'),
  },
  subTabText: { fontSize: 12, fontWeight: '600', color: 'rgba(255,255,255,0.45)' },
  frameBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 7,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  frameBtnActive: { borderColor: uiVar('adm-accent', '#8B5CF6'), backgroundColor: uiVar('adm-accent-wash-15', 'rgba(139,92,246,0.15)') },
  frameBtnText: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  exitBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 7,
    backgroundColor: 'rgba(0,0,0,0.5)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
  },
  // ── Enabled toggle banner ──────────────────────────────────────────────────
  enabledBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    borderRadius: 14, padding: 16, marginBottom: 20,
    borderWidth: 1,
  },
  enabledBannerOn: {
    backgroundColor: 'rgba(16,185,129,0.08)',
    borderColor: 'rgba(16,185,129,0.3)',
  },
  enabledBannerOff: {
    backgroundColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.3)',
  },
  enabledBannerTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '800', marginBottom: 2 },
  enabledBannerSub: { color: 'rgba(255,255,255,0.5)', fontSize: 11, lineHeight: 15 },
  toggleTrack: {
    width: 52, height: 30, borderRadius: 15,
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    padding: 3,
    flexShrink: 0,
  },
  toggleTrackOn: { backgroundColor: '#10B981' },
  toggleTrackOff: { backgroundColor: 'rgba(255,255,255,0.1)' },
  toggleThumb: {
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: '#fff',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3, shadowRadius: 3,
  },
  toggleThumbOn: { alignSelf: 'flex-end' },
  toggleThumbOff: { alignSelf: 'flex-start' },

  desktopFullscreen: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9999,
  },
  desktopExitBtn: {
    position: 'absolute', top: 16, right: 16, zIndex: 10000,
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)',
  },
  previewFrame: {
    borderRadius: 16, overflow: 'hidden',
    borderWidth: 2, borderColor: 'rgba(249,115,22,0.4)',
    minHeight: 620,
  },
  stepList: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
  },
  stepRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  stepNumBubble: {
    width: 28, height: 28, borderRadius: 14,
    backgroundColor: uiVar('adm-accent-wash-20', 'rgba(139,92,246,0.2)'), justifyContent: 'center', alignItems: 'center',
  },
  stepNum: { color: uiVar('adm-accent', '#8B5CF6'), fontWeight: '700', fontSize: 12 },
  stepTitle: { color: uiVar('adm-ink', '#fff'), fontWeight: '500', fontSize: 13 },
  stepDesc: { color: 'rgba(255,255,255,0.5)', fontSize: 11 },
  jumpBtn: {
    width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center',
    backgroundColor: uiVar('adm-accent-wash-12', 'rgba(139,92,246,0.12)'), borderWidth: 1, borderColor: uiVar('adm-accent-wash-30', 'rgba(139,92,246,0.3)'),
  },
  // Analytics
  statsRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  statCard: {
    flex: 1, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', alignItems: 'center',
  },
  statValue: { fontSize: 22, fontWeight: '800' },
  statLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 10, marginTop: 2 },
  statPct: { fontSize: 11, fontWeight: '700', marginTop: 2 },
  funnelCard: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 16,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', marginBottom: 20,
  },
  funnelTitle: { color: uiVar('adm-ink', '#fff'), fontWeight: '700', fontSize: 14, marginBottom: 4 },
  recentRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  refreshBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8,
    borderWidth: 1, borderColor: uiVar('adm-accent-wash-40', 'rgba(139,92,246,0.4)'), backgroundColor: uiVar('adm-accent-wash-8', 'rgba(139,92,246,0.08)'),
  },
  // Config
  cfgSection: {
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', marginBottom: 14,
  },
  cfgSectionTitle: { color: uiVar('adm-ink', '#fff'), fontWeight: '700', fontSize: 13, marginBottom: 4 },
  cfgSectionDesc: { color: 'rgba(255,255,255,0.4)', fontSize: 11, marginBottom: 12 },
  cfgInput: {
    backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: 10,
    color: uiVar('adm-ink', '#fff'), fontSize: 13, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
  },
  cfgFeatureRow: { flexDirection: 'row', gap: 8, marginBottom: 10, alignItems: 'flex-start' },
  cfgAddBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8,
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8,
    borderWidth: 1, borderColor: uiVar('adm-accent-wash-35', 'rgba(139,92,246,0.35)'), borderStyle: 'dashed',
    backgroundColor: uiVar('adm-accent-wash-5', 'rgba(139,92,246,0.05)'),
  },
  cfgChip: {
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  cfgChipActive: { borderColor: uiVar('adm-accent-wash-50', 'rgba(139,92,246,0.5)'), backgroundColor: uiVar('adm-accent-wash-10', 'rgba(139,92,246,0.1)') },
  cfgChipText: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600' },
  cfgBtn: {
    paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
  },
});

